import React from 'react'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'
import { Link } from '@ta-interaktiv/contentbricks'
import { Box } from 'components/Box'
import { StepChart } from 'components/StepChart'

function resizeIframe(obj) {
  obj.style.height =
    obj.contentWindow.document.documentElement.scrollHeight + 'px'
}

export const TEXT_DE = {
  title: <>Hier steht die Schweiz im internationalen Vergleich</>,
  lead: (
    <>
      Die Schweiz hatte eine höhere Übersterblichkeit als Deutschland oder
      Frankreich - dafür weniger Schulschliessungen und eine intakte Wirtschaft.
      Eine Bilanz der Pandemie - in sieben Punkten.
    </>
  ),
  updated: <>Aktualisiert am 11. Juni 2021</>,
  propsinkey: {
    excessmort: 'Übersterblichkeit',
    tests: 'Tests',
    vacc: 'Impfungen',
    sequenced: <>DNA-Sequen&shy;zierung</>,
    gdp: <>Wirt&shy;schafts&shy;wachstum</>,
    stringency: <>Ein&shy;schrän&shy;kungen</>,
    schools: <>Schul&shy;schlie&shy;ssungen</>
  },
  better: 'besser, freier',
  worse: <>schlechter, ein&shy;ge&shy;schränkter</>,
  ave: 'Durchschnitt',
  swiss: 'Schweiz',
  credits: ['Recherche und Text', 'Grafiken und Umsetzung'],
  questions: [
    'Wie informativ war der Beitrag?',
    'Wie gefällt Ihnen die Gestaltung?'
  ],
  intro: (
    <>
      <p>
        Am Freitag, 11. Juni wird der Bundesrat wohl die letzte grosse
        Öffnungsetappe ankündigen. Die Regierung darf optimistisch sein: Dank
        der Impfstoffe leeren sich die Spitäler, die Tage ohne Corona-Todesfälle
        häufen sich. Zeit für eine erste Bilanz. Wie schneidet die Schweiz über
        die ganze Pandemie hinweg ab im Vergleich zu anderen Ländern?
      </p>
      <p>
        Gesundheitsminister Alain Berset hat eine klare Meinung. «Wenn wir
        umliegende Länder anschauen, haben wir das nicht schlecht geschafft»,
        sagte er vor einigen Tagen in der{' '}
        <Link
          allowWordWrap
          href='https://www.srf.ch/play/tv/redirect/detail/d8992c25-824b-469f-8486-251a657ca375?startTime=3227'
        >
          Arena von SRF
        </Link>
        . In einer{' '}
        <Link allowWordWrap href='https://www.youtube.com/watch?v=u6lES79b9kc'>
          YouTube-Sendung für Jugendliche
        </Link>{' '}
        wurde Bundesrat Berset gefragt, wer die Krise schlechter bewältigt habe,
        er oder der Französische Präsident Emmanuel Macron. Bersets Antwort kam
        wie aus der Pistole geschossen: «Macron».
      </p>
      <p>
        Der Bundesrat verschwieg allerdings, dass die Schweiz in der zweiten
        Welle, neben Portugal, die höchste Übersterblichkeit in ganz Westeuropa
        hatte. Dass sie bei Masken und Tests in der ersten Welle eine sehr
        schlechte Figur machte. Wie schneidet die Schweiz also wirklich ab?
      </p>
      <p>
        Wir haben mit Unterstützung von Experten sieben Indikatoren aufgestellt.
        So lässt sich die Schweiz mit ausgewählten europäischen Ländern
        vergleichen. Von den Todesfällen, über Bruttoinlandprodukt bis zur
        Schliessung der Schulen.
      </p>
      <h4>Das Ergebnis</h4>
      <p>
        Im Unterschied zu den meisten Ländern liess man in der Schweiz die
        Wirtschaft weitgehend laufen. Wir schränkten die Versammlungs- und
        Bewegungsfreiheit weniger ein und hielten den Präsenzunterricht an den
        Schulen länger aufrecht als die meisten anderen.{' '}
      </p>
    </>
  ),
  visual: (
    <>
      <p>
        Die drei Kreise auf der rechten Seite der Grafik zeigen diese
        Indikatoren. Dort ist die Schweiz im blauen Bereich, was bedeutet, dass
        sie besser abschneidet als der Durchschnitt der zwölf Länder.
      </p>
      <p>
        Hingegen war sie nicht besonders fleissig mit dem Testen und dem Impfen
        der Bevölkerung (zwei rote Kreise links). Der Kreis in der Mitte zeigt
        die Übersterblichkeit, hier schneidet die Schweiz weniger gut ab. Nur
        dank einer moderaten ersten Welle ist dieser Kreis nicht rot.
      </p>
      <p>
        Der Vergleich der sieben Punkte zeigt, dass die Schweiz bei der
        Bewältigung der Pandemie durchaus mithalten kann.
      </p>
    </>
  ),
  categories: (
    <>
      <Box title='Das Vorgehen'>
        <p>
          Verglichen werden nur westeuropäische Länder, die eine ähnliche
          Epidemiedynamik erlebten wie die Schweiz. Staaten wie Neuseeland oder
          Südkorea gehören deshalb nicht dazu. Die sieben Indikatoren sollen ein
          möglichst breites Spektrum des gesellschaftlichen Lebens abdecken, von
          der Gesundheit über die Wirtschaft und die Wissenschaft bis zu den
          politischen Massnahmen. Bei einzelnen Kriterien sieht man klare
          Unterschiede zwischen den Ländern. Dies soll aber nicht zu einer
          abschliessende Rangliste führen. Die Meinungen, welche Kriterien am
          wichtigsten sind, gehen stark auseinander. Die Farben geben aber einen
          Eindruck, wo ein Land im Verhältnis zu den anderen steht.
        </p>
      </Box>
      <br />
      <p>
        «Es ist ein guter Zeitpunkt, um Bilanz zu ziehen», sagt Antoine
        Flahault, Direktor des Global Health Instituts an der Universität Genf.
        Für ihn sind die Ergebnisse des Vergleichs gut nachvollziehbar. «Man
        muss eine Bilanz stets unter Berücksichtigung der verschiedenen
        wirtschaftlichen, gesellschaftlichen und gesundheitlichen Aspekte
        vornehmen, so wie es hier gemacht wird», sagt Flahault. Er ist ein
        Spezialist für den Vergleich unterschiedlicher staatlicher
        Gesundheitspolitiken und arbeitet oft mit der WHO zusammen. (
        <Link allowWordWrap href='950121274186'>
          siehe Interview
        </Link>
        )
      </p>
      <p>
        Für die Epidemiologin Emma Hodcroft ist es entscheidend, dass der
        Vergleich über die ganze Pandemie geht, nicht nur über eine einzelne
        Phase. «Einige Länder haben gute Entscheidungen getroffen, dann Fehler
        gemacht und kamen dann wieder auf einen besseren Weg», sagt die
        Forscherin der Universität Bern. Nur eine Auswertung auf der Zeitachse
        der gesamten der Pandemie zeigt ein genaues Bild.
      </p>
      <p>
        Auch wenn die Schweiz in einzelnen Punkten gut da steht, belegt ein
        Blick in die einzelnen Kriterien, dass sie das nicht nur der Politik zu
        verdanken hat. Gerade bei den Verstorbenen hätten die Verzögerungen
        zwischen Bund und Kantonen zu einem noch schlimmeren Ergebnis führen
        können.
      </p>
    </>
  ),
  excessmort: (
    <>
      <p>
        Über die ganze Pandemie hinweg betrachtet, hatte Italien, Belgien,
        Portugal und Grossbritannien eine deutlich höhere Übersterblichkeit als
        die Schweiz. In Spanien registrierte man gar 19 Prozent mehr Todesfällen
        als in normalen Zeiten. Die Schweiz hatte 12 Prozent mehr Tote als
        erwartet zu beklagen. Doch das ist noch immer fast doppelt so hoch wie
        Deutschland (6,3 Prozent) und ein vielfaches höher als in Dänemark (nur
        1,6 Prozent mehr).
      </p>
      <p>
        Betrachtet man die Entwicklung der Übersterblichkeit im Laufe der Zeit,
        wird das Bild noch deutlicher: In der ersten Welle waren Italien und
        Frankreich stärker betroffen, weil sie schlicht das Pech hatten, dass
        die Krankheit dort eingeschleppt wurde. Doch in der zweiten Welle, als
        man sich eigentlich vorbereiten konnte, schnitt die Schweiz am
        schlechtesten ab von allen. Es entsteht der Eindruck, dass niemand so{' '}
        <Link allowWordWrap href='471568488340'>
          wenig aus der ersten Pandemiephase gelernt hat
        </Link>{' '}
        wie die Schweiz.
      </p>
      <AutofitIFrame src='https://datawrapper.dwcdn.net/L9Zee/' />
      <p>
        «Im Spätsommer und Herbst, hat die Regierung lange gezögert um mit
        Massnahmen zu reagieren», sagt Christian Althaus, Epidemiologe an der
        Universität Bern. «Dies führte im November und Dezember zu einer sehr
        hohen Anzahl von Todesfällen, die man mit einer etwas früheren
        Einführung von Obergrenzen für Versammlungen und Veranstaltungen hätte
        vermeiden können.»
      </p>
      <p>
        Eigentlich hätte man erwarten können, dass die Schweiz hier besser
        abschneidet: Wir haben nicht nur eines der leistungsfähigsten
        Gesundheitssysteme der Welt, sondern auch einen kleineren Anteil von
        über 65-Jährigen als die meisten Länder Europas. Der grösste Teil der
        Todesfälle gab es aber bei den Älteren.
      </p>
    </>
  ),
  tests: (
    <>
      <p>
        Will man die Infektionsketten unterbrechen und die Ausbreitung der
        Epidemie verlangsamen, führt kein Weg an Tests vorbei. «Testen, testen
        Testen», war das Credo seit Anfang der Pandemie. Dänemark, Österreich
        und Grossbritannien befolgten das am besten. Sie heben sich bei der
        Anzahl Tests pro Kopf vom Rest Europas deutlich ab. Die Schweiz hingegen
        gehört zu den Schlusslichtern.
      </p>
      <p>
        Dabei betonte eine Gruppe von 13 Wissenschafterinnen und Wissenschafter
        bereits im März 2020, wie wichtig es für die Schweiz sei, eine
        starke Test-Strategie zu fahren.
      </p>
      <p>
        Der Bundesrat und das BAG jedoch zögerten und brauchten mehr als drei
        Monate, um einen Plan für kostenlose Tests vorzulegen. Danach dauerte es
        noch bis April 2021, bis gratis Selbsttests an die Bevölkerung abgegeben
        wurden. Österreich zum Beispiel hatte hier stets mehrere Wochen
        Vorsprung.
      </p>
      <AutofitIFrame src='https://datawrapper.dwcdn.net/wBqlk/' />

      <p>
        «Ob ein Land genug getestet hat, zeigt vor allem die sogenannte
        Positivitätsrate, also die Anzahl der positiven Tests pro 100
        durchgeführte Tests», erklärt der EPFL-Virologe Didier Trono. Je höher
        diese Rate ist, desto mehr ist die Epidemie ausser Kontrolle geraten.
        Anders als in der ersten Welle hatte die Schweiz zu Beginn der zweiten
        Welle genügend Tests und auch schon viel Erfahrung. Dennoch kamen wir im
        November 2020 auf eine Positivitätsrate von 27 Prozent. In Frankreich
        und Italien lag man bei 15, in Deutschland bei 8 und in Dänemark sogar
        nur bei 2 Prozent. Fazit: Die Schweiz testete viel zu wenig und verlor
        die Kontrolle.
      </p>
      <AutofitIFrame src='https://datawrapper.dwcdn.net/Cgjku/' />

      <p>
        Dänemark, Norwegen und Finnland waren damals die einzigen drei
        europäischen Länder, die es geschafft hatten, das Virus nicht nur
        einzudämmen, sondern an vielen Orten gar zu eliminieren.
      </p>
    </>
  ),
  vacc: (
    <>
      <p>
        Obwohl die Schweiz in den letzten Tagen am meisten Personen pro 100
        Einwohner in Europa impft, ist sie noch immer im Mittelfeld mit ihren
        Nachbarn und hat erst recht nicht zu Grossbritannien aufgeholt.
      </p>
      <AutofitIFrame src='https://datawrapper.dwcdn.net/E5J7B/' />
      <p>
        Die Britische Regierung setzte sehr früh auf einen billigen Impfstoff,
        der einfacher zu produzieren und zu verteilen war. Zudem entschieden
        sich die Briten, den Abstand zwischen der ersten und zweiten Dosis so
        weit wie möglich zu vergrössern, um so schnell wie möglich eine breite
        Immunität zu erreichen. Diese Strategie scheint sich auszuzahlen.
        Andererseits birgt dieses Vorgehen auch ein Risiko. Neue, besonders
        ansteckende Covid-Varianten lassen sich so womöglich{' '}
        <Link allowWordWrap href='486669329559'>
          weniger gut eindämmen
        </Link>
        .
      </p>
      <p>
        Die Schweiz hingegen hat sich als eines der wenigen Länder sehr schnell
        für mRNA-Impfstoffe entschieden, eine modernere Technologie, die - wie
        bisherige Studien zeigen - wirksamer ist als andere.
      </p>
      <p>
        Doch für Experte Flahault hätte die Schweiz hier viel schneller sein
        müssen. «Es ist unverständlich, dass ein Land wie die Schweiz, das über
        vergleichbare Mittel wie Israel verfügt und schnell Impfstoffdosen hätte
        beschaffen können, hier nicht die gleiche Beweglichkeit an den Tag
        legte.»
      </p>
    </>
  ),
  sequenced: (
    <>
      <p>
        Es ist nicht leicht, den Beitrag der Wissenschaft eines Landes im Kampf
        gegen die Pandemie zu messen. Eine Möglichkeit ist, den Anteil positiver
        Tests anzuschauen, für die eine DNA-Sequenzierung durchgeführt wurde.
        Für die weltweite Forschung ist dies entscheidend. Die DNA eines Virus
        ändert sich laufend, dabei entsteht ein eigentlicher Viren-Stammbaum,
        den man nur durch breite Sequenzierung in vielen Ländern gut erforschen
        kann.
      </p>
      <p>
        Dies ermöglicht einerseits ein besseres Verständnis der Ausbreitung der
        Krankheit, indem die Übertragungskette präzis nachverfolgt werden kann.
        «Auf der anderen Seite geht es darum, ein Frühwarnsystem für den Fall zu
        haben, dass ein Stamm auftaucht, gegen den die verschiedenen Impfstoffe
        nicht wirken», erklärt die Forscherin Emma Hodcroft. «Das ist besonders
        wichtig für die Zukunft.» Hodcroft ist eine der Hauptverantwortlichen
        für Nextstrain, eine globale Datenbank mit den DNA-Sequenzen des Virus.
      </p>
      <p>
        Die Sequenzierungs-Rate eines Landes berücksichtigt weder die
        wissenschaftlichen Publikationen der Forschenden zur Krankheit, noch den
        Beitrag zur Entwicklung von Behandlungsmethoden oder Impfstoffen. Der
        Indikator ist dafür gut vergleichbar unter vielen Ländern.
      </p>
      <p>
        Die Schweiz hat seit Beginn der Pandemie rund 6 Prozent ihrer positiven
        Proben sequenziert. Damit liegt sie an dritter Stelle in unserem
        Vergleich, hinter Dänemark (37 Prozent) und Grossbritannien (10
        Prozent).
      </p>
      <p>
        Diese gute Position ist aber vor allem den freiwilligen Bemühungen der
        Hochschullabore zu verdanken. Erst im Mai 2021 hat das BAG angekündigt,
        die DNA-Sequenzierung im grossen Stil finanziell zu unterstützen. «Wir
        machen das in gewisser Weise schon seit langem pro-bono», sagt Didier
        Trono von der EPFL. «Wir haben bereits im letzten Sommer auf die
        Bedeutung der Sequenzierung hingewiesen.»
      </p>
      <p>
        Im Klartext: Die gute Position der Schweiz ist kein Verdienst der
        Politik oder des Bundes, sondern der Universitäten und der ETHs.
      </p>
    </>
  ),
  gdp: (
    <>
      <p>
        Das Konjunkturpaket des Bundes war unbürokratischer und gleichzeitig
        wirksamer als fast alle Massnahmen der anderen Länder im Vergleich. Mit
        über 60 Milliarden Franken war es aber auch eines der teuersten.
      </p>
      <p>
        So erlebte die Schweiz im Jahr 2020 im Vergleich zu den meisten anderen
        europäischen Ländern eine mildere Rezession (-3 Prozent des
        Bruttoinlandsproduktes BIP). Und die Prognosen für 2021 sind gut (+3,5
        Prozent des BIP).
      </p>
      <p>
        Über die beiden Krisenjahre 2020 und 2021 gesehen sollte die
        Eidgenossenschaft gemäss Voraussagen sogar ein leichtes Wachstum von 0,4
        Prozent verzeichnen. Nur Schweden hat mit 0,2 Prozent ebenfalls leicht
        positive Wachstumsaussichten. Deutschland (-1,5 Prozent) wird demnach
        einen Rückgang des BIP erleben, sowie Frankreich (-2,9), Italien,
        Grossbritannien und Spanien (alle drei -5 Prozent).
      </p>
      <p>
        «Aus wirtschaftlicher Sicht ist die Leistung der Schweiz während dieser
        Pandemie aussergewöhnlich in Europa», hält Professor Flahault fest.
      </p>
    </>
  ),
  schools: (
    <>
      <p>
        Das Coronavirus zirkuliert unter jungen Menschen genauso wie in der
        übrigen Gesellschaft. Das ist inzwischen{' '}
        <Link allowWordWrap href='516982617589'>
          wissenschaftlich erwiesen
        </Link>
        . Doch der Bund und die Kantone haben nach der ersten Welle immer
        gezögert, den Präsenzunterricht ein zweites mal einzustellen. Man war
        der Meinung, dass dies nur im äussersten Notfall geschehen sollte.
      </p>
      <p>
        «Auch die wissenschaftliche Taskforce hat diese Position auf der Basis
        der vorliegenden Daten unterstützt», sagt deren Vizepräsidentin, die
        Bioethik-Professorin Samia Hurst-Majno.
      </p>
      <p>
        Eine längere Schliessung der Schulen hätte zwar die Gesundheit der
        Kinder und Jugendlichen und ihrer Eltern geschützt, räumt Hurst-Majno
        ein. Aber sie hätte auch den Zugang zu Bildung eingeschränkt – besonders
        für Betroffene aus bildungsfernen Milieus.
      </p>
      <p>
        Die folgende Grafik zeigt, dass die Schweiz länger als Deutschland mit
        der Schliessung von Schulen und Hochschulen wartete und diese schneller
        wieder öffnete. Im Vergleich dazu hatten Italien, Deutschland und sogar
        Frankreich viel längere Schliessungszeiten.
      </p>
      <StepChart
        locale='DE'
        locales={{
          title: 'Schul- und Hochschulschliessungen',
          sub: 'Skala von 0 bis 3 gemäss Oxford Government Response Tracker',
          options: [
            '0: Alle Schulen offen',
            '1: Leichte Einschränkungen des Schulbetriebs',
            '2: Einzelne Schulen geschlossen',
            '3: Alle Schulen geschlossen'
          ]
        }}
      />{' '}
      <p>
        «Grundsätzlich zeigt ein Land seine Prioritäten in dem, was es als
        letztes schliesst», sagt Hurst-Majno. «In der Schweiz sind die Schulen
        eine dieser Prioritäten.»
      </p>
    </>
  ),
  stringency: (
    <>
      <p>
        Kaum ein anderes Land in Europa hatte so wenige Einschränkungen der
        individuellen Freiheiten wie die Schweiz. Das gilt besonders im
        Vergleich zu Frankreich und Italien, aber auch zu Deutschland und
        Österreich.
      </p>
      <p>
        Um den Schweregrad dieser Einschränkungen zu messen, haben
        Wissenschaftler der Universität Oxford einen Massnahmen-Index definiert,
        der auf neun Indikatoren basiert. Dazu gehören die Verpflichtung zum
        Homeoffice, die Einschränkung von öffentlichen Veranstaltungen, das
        Verbot von Versammlungen, Reisebeschränkungen und anderes.
      </p>
      <p>
        Mit durchschnittlich 53 von 100 Punkten auf diesem Massnahmen-Index für
        die gesamte Pandemie, hatte die Schweiz deutlich weniger Einschränkungen
        als Frankreich (65), Deutschland (67) oder Italien (72). Selbst
        Schweden, das in der ersten Welle wenig eingriff, war über die
        Gesamtdauer der Pandemie strenger, mit einem Index von 61.
      </p>
      <p>
        Von Februar 2020 bis Mai diesen Jahres wurden in der Schweiz fast immer
        die sanftesten Massnahmen in ganz Europa ergriffen. Nur Dänemark hatte
        der Bevölkerung während einer kurzen Periode zwischen November und
        Dezember 2020 noch geringere Einschränkungen auferlegt.
      </p>
      <AutofitIFrame src='https://datawrapper.dwcdn.net/qLNed/' />
    </>
  ),

  end: (
    <>
      <h4>Fazit</h4>
      <p>
        Die Regierungen aller Länder haben die Menschen aufgefordert, ihr
        Verhalten zu ändern, andere Entscheidungen zu treffen, Lasten zu tragen
        und Opfer für das Gemeinwohl zu bringen. «Im Gegenzug haben sie sich
        verpflichtet, das Leben, die körperliche Unversehrtheit der Menschen so
        weit wie möglich vor der Pandemie zu schützen», sagt Bioethikerin
        Hurst-Majno. «Die Regierungen haben sich aber ebenfalls verpflichtet,
        die Rechte der Einwohner zu schützen und uns wo immer möglich eine
        Brücke zu bauen, damit wir materiell möglichst unbeschadet auf der
        anderen Seite ankommen.»
      </p>
      <p>
        Gemessen daran, scheint die Schweiz im Vergleich zu ihren Nachbarn die
        Krise nicht allzu schlecht bewältigt zu haben.
      </p>
      <p>
        Aber für Hurst-Majno kann eine abschliessende Bilanz erst später gezogen
        werden. Viele Daten müsse man noch studieren. Die wirtschaftliche Lage
        der Haushalte, insbesondere der schwächeren, könne sich stark von der
        Lage der Gesamtwirtschaft unterscheiden. Es ist auch nicht bekannt, wie
        viele Menschen unter dem Long-Covid-Syndrom leiden.
      </p>
      <p>
        Andere Elemente wie das Gefühl des nationalen Zusammenhalts, das
        Vertrauen, dass «mein Land für mich da war und ich für es da war», sind
        ebenfalls schwierig zu messen. «Eine Pandemie ist eine Zeit, in der es
        viele Möglichkeiten gibt, sich zurückgesetzt zu fühlen», sagt
        Hurst-Majno. «Und das kann Spuren hinterlassen.»
      </p>

      <div style={{ paddingBottom: '56%', position: 'relative' }}>
        <iframe
          data-mimetype='x-videocms/video'
          data-id='uv436221'
          data-duration='602'
          data-preview-url='https://unitythumb.appuser.ch/frames/uv436221/frame-1-436221.jpg'
          data-title='1 Jahr Corona – der Rückblick'
          data-lead='Vier Personen schauen auf ein Jahr Coronavirus in der Schweiz zurück: Unternehmerin Yen Han, Bieler Stadtpräsident Erich Fehr, Direktorin des Spitals Schwyz Franziska Föllmi-Heusi und TagesAnzeiger-Nachrichtenchef Matthias Chapman'
          src='https://www.tagesanzeiger.ch/extern/videoplayer/videoplayer-nn.html?params=client@tagesanzeiger%7CvideoId@436221%7CshowLogo@1%7CautoStart@0%7Cmute@0%7CshowAds@1%7CpreviewPath@https://server025.newsnetz.tv/436221/frame-1-436221.jpg%7Cplatform@desktop'
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            border: 0
          }}
        ></iframe>
      </div>
      <br/>
    </>
  ),
  method: (
    <Box title='Quellen'>
      <div className='sources'>
        <p>
          <b>Übersterblichkeit</b>
          <span className='source'>
            Quelle:{' '}
            <Link
              allowWordWrap
              href='https://ourworldindata.org/excess-mortality-covid'
            >
              Our World in Data
            </Link>
          </span>
          Berechnet wird die durchschnittliche Übersterblichkeit zwischen März
          2020 und April 2021. Dabei wird in jeder Woche die effektive Anzahl
          der Todesfälle mit dem langfristigen Mittelwert (2015 bis 2019) für
          diese Woche verglichen. Für Italien liegen die Werte nur bis Ende
          Februar 2021 vor. Die Übersterblichkeit wird dort in dem Vergleich
          somit womöglich etwas überschätzt.
        </p>
        <p>
          <b>Massnahmen</b>
          <span className='source'>
            Quelle:{' '}
            <Link
              allowWordWrap
              href='https://github.com/OxCGRT/covid-policy-tracker/blob/master/documentation/codebook.md'
            >
              Oxford Government Response Tracker
            </Link>
          </span>
          Mit dem Oxford Stringency Index wird festgehalten, wie stark der Staat
          das öffentliche und private Leben einschränkt. Berücksichtigt werden
          unter anderem Ausgangssperren, Einschränkung internationaler Reisen
          oder Homeoffice Pflicht. Gezeigt wird der durchschnittliche Wert
          zwischen Februar 2020 und Mai 2021. 0 bedeutet keine Einschränkungen
          und 100 maximale Einschränkungen.
        </p>
        <p>
          <b>Schul- und Hochschulschliessung</b>
          <span className='source'>
            Quelle:{' '}
            <Link
              allowWordWrap
              href='https://github.com/OxCGRT/covid-policy-tracker/blob/master/documentation/codebook.md'
            >
              Oxford Government Response Tracker
            </Link>
          </span>
          Schulschliessungen werden im Rahmen des Oxford Stringency Index
          ebenfalls erfasst. Hier gezeigt wird der durchschnittliche Wert
          zwischen Februar 2020 und Mai 2021. 0 Bedeutet keine Einschränkung des
          Schulbetriebes, 3 Schliessung des gesamten Präsenzunterrichts.
        </p>
        <p>
          <b>Wirtschaftswachstum</b>
          <span className='source'>
            Quelle:{' '}
            <Link
              allowWordWrap
              href='https://www.imf.org/external/datamapper/NGDP_RPCH@WEO/ADVEC/AUT/DEU/GBR/CHE'
            >
              Internationaler Währungsfond
            </Link>
          </span>
          Der internationale Währungsfond rapportiert das Wirtschaftswachstum
          (in % gemessen am Bruttoinlandprodukt) für das Jahr 2020, sowie eine
          Prognose für das Jahr 2021. Das kombinierte Wachstum für die Jahre
          2020 und 2021 wird hier aufgezeigt.
        </p>
        <p>
          <b>DNA Sequenzierung</b>
          <span className='source'>
            Quelle:{' '}
            <Link allowWordWrap href='https://www.gisaid.org/index.php?id=208'>
              GISAID
            </Link>
          </span>
          Eine rasche Sequenzierung der DNA von möglichst vielen positiven
          Corona-Tests ist wichtig, um die Ausbreitung und Weiterentwicklung des
          Viruses zu verstehen. Ausbreitungsketten können so nachvollzogen und
          neue Mutationen frühzeitig erkannt werden. Je mehr Proben sequenziert
          werden, desto grösser ist der Beitrag an die internationale
          Covid-Forschung. Auf gisaid.org erfasste Sequenzierungen bis zum 8.
          Juni wurden hier erfasst.
        </p>
        <p>
          <b>Impfung</b>
          <span className='source'>
            Quelle:{' '}
            <Link
              allowWordWrap
              href='https://ourworldindata.org/covid-vaccinations'
            >
              Our World in Data
            </Link>
          </span>
          Hier wird gemessen, wie viele Impfdosen bis Ende Mai 2021 pro 100
          Personen verabreicht wurden. Dabei wird nicht unterschieden, ob jemand
          zwei Impfdosen erhalten hat oder zwei Personen je eine. Diese Zahl
          unterscheidet sich also von der Zahl der vollständig oder einfach
          Geimpften.
        </p>
        <p>
          <b>Tests</b>
          <span className='source'>
            Quelle:{' '}
            <Link
              allowWordWrap
              href='https://ourworldindata.org/coronavirus-testing'
            >
              Our World in Data
            </Link>
          </span>
          Berücksichtigt werden PCR Tests und Antigen-Schnelltests. Nicht
          berücksichtigt werden Antikörper-Tests welche zeigen, ob eine Person
          in der Vergangenheit dem Virus ausgesetzt war, sowie Selbsttests die
          nicht an eine zentrale Instanz gemeldet werden. Schweden rapportiert
          nur PCR Tests.
        </p>
      </div>
    </Box>
  )
}
